import { useEffect, useMemo, useState } from 'react'
import { Button } from '@jana-technology/platform-ui/button'
import Modal, { ModalClose } from '@jana-technology/platform-ui/modal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {
  IAcceptanceCategory,
  RiskThresholdLevelCode,
  useGetAcceptanceCategoriesQuery,
  useUpdateAcceptanceCategoriesMutation
} from 'app/services/api/acceptanceCategories'

import { AttributeSelector } from './AttributeSelector'
import { ThresholdEditor } from './ThresholdEditor'
import { IThresholdConfigurationError, RiskThresholdType } from './types'

export const EditThreshold = () => {
  const { data: riskThresholds } = useGetAcceptanceCategoriesQuery()
  const [updateAcceptanceCategories] = useUpdateAcceptanceCategoriesMutation()

  const [open, setOpen] = useState(false)
  const [selectedRiskType, setSelectedRiskType] = useState<RiskThresholdType>(
    RiskThresholdType.Risk
  )
  const [riskThresholdStage, setRiskThresholdStage] = useState<
    IAcceptanceCategory[]
  >([])
  const [thresholdErrors, setThresholdErrors] =
    useState<IThresholdConfigurationError>({
      minMaxValueErrorRows: [],
      minMaxDensityErrorRows: [],
      continuityValueErrorRows: [],
      continuityDensityErrorRows: []
    })

  const hasRiskAssessments = riskThresholdStage.length > 0

  const isThresholdsValid = useMemo(() => {
    return (
      thresholdErrors.minMaxValueErrorRows.length === 0 &&
      thresholdErrors.minMaxDensityErrorRows.length === 0 &&
      thresholdErrors.continuityValueErrorRows.length === 0 &&
      thresholdErrors.continuityDensityErrorRows.length === 0
    )
  }, [thresholdErrors])

  useEffect(() => {
    if (riskThresholds) {
      setRiskThresholdStage([...riskThresholds])
    }
  }, [riskThresholds, open])

  useEffect(() => {
    const order: RiskThresholdLevelCode[] = [
      RiskThresholdLevelCode.LowRisk,
      RiskThresholdLevelCode.MediumLowRisk,
      RiskThresholdLevelCode.MediumRisk,
      RiskThresholdLevelCode.MediumHighRisk,
      RiskThresholdLevelCode.HighRisk
    ]

    const sortedThresholds = [...riskThresholdStage]
      .sort((a, b) => order.indexOf(a.code) - order.indexOf(b.code))
      .filter((threshold) => threshold.enabled)

    const minMaxValueErrorRows: number[] = []
    const minMaxDensityErrorRows: number[] = []
    const continuityValueErrorRows: number[] = []
    const continuityDensityErrorRows: number[] = []

    for (let i = 0; i < sortedThresholds.length - 1; i++) {
      const currentRow = sortedThresholds[i]
      const nextRow = sortedThresholds[i + 1]

      // Min/Max checks
      if (currentRow.minValue > currentRow.maxValue) {
        minMaxValueErrorRows.push(i)
      }

      if (currentRow.minDensityValue > currentRow.maxDensityValue) {
        minMaxDensityErrorRows.push(i)
      }

      // Continuity checks
      if (nextRow.minValue !== currentRow.maxValue) {
        continuityValueErrorRows.push(i)
      }

      if (nextRow.minDensityValue !== currentRow.maxDensityValue) {
        continuityDensityErrorRows.push(i)
      }
    }

    setThresholdErrors({
      minMaxValueErrorRows,
      minMaxDensityErrorRows,
      continuityValueErrorRows,
      continuityDensityErrorRows
    })
  }, [riskThresholdStage])

  const onSubmit = () => {
    // Prepare payload for API
    const payload = {
      categories: riskThresholdStage.map(
        ({ code, minValue, maxValue, minDensityValue, maxDensityValue }) => ({
          code,
          minValue,
          maxValue,
          minDensityValue,
          maxDensityValue
        })
      )
    }

    updateAcceptanceCategories(payload)
  }

  return (
    <>
      <Button
        kind='primary'
        disabled={!hasRiskAssessments}
        onClick={() => setOpen(true)}
      >
        Edit Thresholds
      </Button>

      <Modal
        open={open}
        onOpenChange={() => setOpen(false)}
        kind='complex'
        title='Edit Thresholds'
        sidebar={
          <AttributeSelector
            attributeList={Object.values(RiskThresholdType).map((name) => {
              let unit = undefined

              switch (name) {
                case RiskThresholdType.Risk:
                  unit = 'RU/yr'

                  break
                case RiskThresholdType.RiskDensity:
                  unit = 'RU/mi/yr'

                  break
              }

              return { name, unit }
            })}
            selectedAttribute={selectedRiskType}
            setSelectedAttribute={(riskTypeName) => {
              setSelectedRiskType(riskTypeName as RiskThresholdType)
            }}
          >
            Risk Definition
          </AttributeSelector>
        }
        actions={{
          primary: (
            <>
              <ModalClose asChild>
                <Button kind='ghost' className='mr-1'>
                  Cancel
                </Button>
              </ModalClose>
              <ModalClose asChild disabled={!isThresholdsValid}>
                <Button kind='primary' onClick={onSubmit}>
                  Save
                </Button>
              </ModalClose>
            </>
          )
        }}
      >
        <div className='flex flex-col justify-between min-h-50vh'>
          <ThresholdEditor
            riskType={selectedRiskType}
            riskThresholds={riskThresholdStage}
            onRiskThresholdsChange={(updatedThresholds) => {
              setRiskThresholdStage(updatedThresholds)
            }}
          />
          <div
            className={[
              'flex items-center px-2 py-3',
              'border rounded border-blue',
              'text-blue'
            ].join(' ')}
          >
            <InfoOutlinedIcon className='mx-2' />
            <div className='ml-2 text-sm font-medium'>
              Threshold ranges must be continuous, with no gaps or overlaps
              between risk levels.
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
